import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  child,
  records,
  wishes,
  templates,
  formatDate,
} from "@/lib/mock-data";
import { useAppStore } from "@/lib/app-store";

export const Route = createFileRoute("/app/home")({
  component: HomePage,
});

function HomePage() {
  const { role } = useAppStore();
  const [description, setDescription] = useState("");
  const [points, setPoints] = useState<number | "">(3);
  const [category, setCategory] = useState("阅读");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTip, setAiTip] = useState(false);
  const [praiseOnly, setPraiseOnly] = useState(false);
  const [reminder, setReminder] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);

  const isViewer = role === "viewer";

  const runAI = async () => {
    if (!description.trim()) {
      toast("请先写几个字,AI 才能帮你整理");
      return;
    }
    setAiLoading(true);
    // simulate
    await new Promise((r) => setTimeout(r, 900));
    // fake success 90%
    if (Math.random() > 0.15) {
      const cleaned = description.trim().replace(/\s+/g, "") + "(已整理)";
      setDescription(cleaned);
      setPoints(3);
      setCategory("习惯");
      setAiTip(true);
    } else {
      toast.error("AI 整理失败,请手动填写");
    }
    setAiLoading(false);
  };

  const confirm = () => {
    if (!description.trim()) {
      toast("请填写描述");
      return;
    }
    toast.success(praiseOnly ? "已记录一次口头表扬" : `已记录 +${points || 0} 分`);
    setDescription("");
    setPoints(3);
    setAiTip(false);
  };

  const applyTemplate = (t: (typeof templates)[number]) => {
    setDescription(t.label);
    setPoints(t.points);
    setCategory(t.category);
    setAiTip(false);
  };

  const todayRecords = records.slice(0, 3);
  const topWishes = wishes.slice(0, 2);

  return (
    <div className="pb-6">
      {/* Header */}
      <header className="px-5 pb-4 pt-6">
        <p className="text-[var(--fs-sm)]" style={{ color: "var(--muted)" }}>
          {child.name} · 可用点数
        </p>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-[2.5rem] font-semibold leading-none tracking-tight">
            {child.availablePoints}
          </span>
          <span
            className="text-[var(--fs-sm)]"
            style={{ color: "var(--muted)" }}
          >
            分
          </span>
        </div>
      </header>

      {/* Expiring soon */}
      {child.expiringSoon > 0 && (
        <div
          className="mx-5 mb-4 flex items-center gap-2 rounded-[var(--r-md)] border px-3 py-2 text-[var(--fs-sm)]"
          style={{ background: "var(--warn-soft)", borderColor: "var(--warn)" }}
        >
          <AlertTriangle size={16} style={{ color: "var(--warn-fg)" }} />
          <span style={{ color: "var(--warn-fg)" }}>
            有 {child.expiringSoon} 分将在 7 天内过期,尽快兑换心愿吧
          </span>
        </div>
      )}

      {/* Quick record card */}
      {isViewer ? (
        <div className="mx-5 rounded-[var(--r-lg)] border bg-surface p-6 text-center">
          <p style={{ color: "var(--muted)" }}>你目前是观察者身份,无法记录</p>
        </div>
      ) : (
        <section className="mx-5 rounded-[var(--r-lg)] border bg-surface p-4 shadow-sm">
          {aiTip && (
            <div
              className="mb-3 flex items-start justify-between gap-2 rounded-[var(--r-sm)] px-3 py-2 text-[var(--fs-xs)]"
              style={{ background: "var(--accent-soft)", color: "var(--fg)" }}
            >
              <span>已用 AI 整理,你可以修改后再确认</span>
              <button
                onClick={() => setAiTip(false)}
                style={{ color: "var(--muted)" }}
                aria-label="dismiss"
              >
                ✕
              </button>
            </div>
          )}
          <div className="relative">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="记下这次的具体表现,例如:主动读了 20 分钟课外书"
              className="min-h-[92px] resize-none pr-12"
            />
            <button
              onClick={runAI}
              disabled={aiLoading}
              aria-label="AI 整理"
              className="absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full border transition-opacity disabled:opacity-60"
              style={{
                background: "var(--accent-soft)",
                borderColor: "var(--accent)",
                color: "var(--accent)",
              }}
            >
              {aiLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Sparkles size={16} />
              )}
            </button>
          </div>

          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1">
              <Label htmlFor="pts" className="mb-1 text-[var(--fs-xs)]">
                点数
              </Label>
              <Input
                id="pts"
                type="number"
                inputMode="numeric"
                min={0}
                max={99}
                value={points}
                onChange={(e) =>
                  setPoints(e.target.value === "" ? "" : Number(e.target.value))
                }
                disabled={praiseOnly}
                className="h-10"
              />
            </div>
            <Button className="h-11 flex-[2] text-base" onClick={confirm}>
              确认记录
            </Button>
          </div>

          {/* Templates */}
          <div className="-mx-4 mt-4">
            <div
              className="flex gap-2 overflow-x-auto px-4 pb-1"
              style={{ scrollbarWidth: "none" }}
            >
              {templates.map((t) => (
                <button
                  key={t.label}
                  onClick={() => applyTemplate(t)}
                  className="shrink-0 rounded-full border px-3 py-1.5 text-[var(--fs-xs)]"
                  style={{ background: "var(--surface-2)" }}
                >
                  {t.label}{" "}
                  <span style={{ color: "var(--accent)" }}>+{t.points}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Advanced */}
          <button
            onClick={() => setAdvOpen((v) => !v)}
            className="mt-3 flex w-full items-center justify-between rounded-[var(--r-sm)] px-1 py-2 text-[var(--fs-sm)]"
            style={{ color: "var(--muted)" }}
          >
            <span>高级选项</span>
            {advOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {advOpen && (
            <div className="space-y-3 border-t pt-3">
              <div>
                <Label className="mb-1 text-[var(--fs-xs)]">类别</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["阅读", "作业", "家务", "学校", "练琴", "习惯", "礼仪"].map(
                      (c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 text-[var(--fs-xs)]">过期时间(天)</Label>
                <Input type="number" defaultValue={30} className="h-10" />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="praise">仅口头表扬(不计分)</Label>
                <Switch
                  id="praise"
                  checked={praiseOnly}
                  onCheckedChange={setPraiseOnly}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="rem">作为提醒记录</Label>
                <Switch id="rem" checked={reminder} onCheckedChange={setReminder} />
              </div>
              <div>
                <Label className="mb-1 text-[var(--fs-xs)]">标签</Label>
                <Input placeholder="用空格分隔,如:主动 认真" className="h-10" />
              </div>
              <div>
                <Label className="mb-1 text-[var(--fs-xs)]">备注</Label>
                <Textarea placeholder="额外备注" className="min-h-[60px]" />
              </div>
            </div>
          )}
        </section>
      )}

      {/* Today records */}
      <section className="mt-6 px-5">
        <SectionHeading
          title="今天的记录"
          to="/app/records"
          right={`共 ${todayRecords.length} 条`}
        />
        {todayRecords.length === 0 ? (
          <div
            className="rounded-[var(--r-md)] border border-dashed px-4 py-6 text-center text-[var(--fs-sm)]"
            style={{ color: "var(--muted)" }}
          >
            今天还没有记录,选一个模板开始吧
          </div>
        ) : (
          <ul className="space-y-2">
            {todayRecords.map((r) => (
              <li key={r.id}>
                <Link
                  to="/app/records/$id"
                  params={{ id: r.id }}
                  className="block rounded-[var(--r-md)] border bg-surface p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-[var(--fs-sm)] font-medium">
                        {r.description}
                      </p>
                      <p
                        className="mt-0.5 text-[var(--fs-xs)]"
                        style={{ color: "var(--muted)" }}
                      >
                        {r.category} · {formatDate(r.createdAt)} · {r.createdBy}
                      </p>
                    </div>
                    <PointsChip record={r} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Wishes preview */}
      <section className="mt-6 px-5">
        <SectionHeading title="心愿进度" to="/app/wishes" />
        <ul className="space-y-2">
          {topWishes.map((w) => {
            const pct = Math.min(100, (child.availablePoints / w.required) * 100);
            return (
              <li key={w.id}>
                <Link
                  to="/app/wishes/$id"
                  params={{ id: w.id }}
                  className="block rounded-[var(--r-md)] border bg-surface p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="min-w-0 truncate text-[var(--fs-sm)] font-medium">
                      {w.title}
                    </p>
                    <span
                      className="shrink-0 text-[var(--fs-xs)]"
                      style={{ color: "var(--muted)" }}
                    >
                      {child.availablePoints}/{w.required}
                    </span>
                  </div>
                  <Progress value={pct} className="mt-2 h-1.5" />
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function SectionHeading({
  title,
  to,
  right,
}: {
  title: string;
  to: "/app/records" | "/app/wishes";
  right?: string;
}) {
  return (
    <div className="mb-2 flex items-baseline justify-between">
      <h2 className="text-[var(--fs-md)] font-semibold">{title}</h2>
      <Link
        to={to}
        className="text-[var(--fs-xs)]"
        style={{ color: "var(--accent)" }}
      >
        {right || "查看全部"} ›
      </Link>
    </div>
  );
}

function PointsChip({ record }: { record: (typeof records)[number] }) {
  if (record.kind === "praise")
    return (
      <span
        className="shrink-0 rounded-full px-2 py-0.5 text-[var(--fs-xs)]"
        style={{ background: "var(--surface-2)", color: "var(--muted)" }}
      >
        表扬
      </span>
    );
  if (record.kind === "reminder")
    return (
      <span
        className="shrink-0 rounded-full px-2 py-0.5 text-[var(--fs-xs)]"
        style={{ background: "var(--warn-soft)", color: "var(--warn-fg)" }}
      >
        提醒
      </span>
    );
  return (
    <span
      className="shrink-0 rounded-full px-2 py-0.5 text-[var(--fs-xs)] font-medium"
      style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
    >
      +{record.points}
    </span>
  );
}
